import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  CreditCard,
  TrendingDown,
} from "lucide-react";
import React from "react";
import { Button } from "../../misc/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../../misc/Card";
import { CurrentPlan } from "../types";

interface CurrentPlanCardProps {
  currentPlan: CurrentPlan | undefined;
  onChangePlan: () => void;
  onNavigateToHelp: () => void;
  onBackToSettings: () => void;
  isImpersonated?: boolean;
}

const CurrentPlanCard: React.FC<CurrentPlanCardProps> = ({
  currentPlan,
  onChangePlan,
  onNavigateToHelp,
  onBackToSettings,
  isImpersonated,
}) => {
  const formatCurrency = (amount: number, currency: string = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount / 100);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  // If currentPlan is not available, show fallback UI
  if (!currentPlan) {
    return (
      <Card className="w-full max-w-4xl mx-auto overflow-hidden border shadow-sm ">
        {/* Back to Settings Button */}
        <div className="p-4 bg-white">
          <Button
            onClick={onBackToSettings}
            className="flex items-center gap-2"
            id="back-to-settings"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Settings
          </Button>
        </div>

        <CardContent className="p-8 bg-white">
          <div className="text-center space-y-1">
            <h2 className="text-2xl font-bold text-primary">Current Plan</h2>

            <div className="space-y-4">
              <p className="text-gray-600">
                No active subscription plan found.
              </p>

              <div className="flex items-baseline justify-center gap-1">
                <span className="text-5xl font-bold text-yellow-600">$0</span>
                <span className="text-lg text-gray-500">/month</span>
              </div>

              <p className="text-sm text-gray-600">
                Please contact us via{" "}
                <button
                  onClick={onNavigateToHelp}
                  className="text-primary hover:text-secondary font-semibold underline"
                >
                  help
                </button>{" "}
                page to re-subscribe.
              </p>

              <Button
                onClick={onChangePlan}
                className="w-full mt-4 py-3 text-base font-semibold"
                disabled={isImpersonated}
              >
                Resubscribe
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasDiscount =
    currentPlan.lastPayment && currentPlan.lastPayment.discountApplied > 0;
  const hasUpcomingDiscount =
    currentPlan.upcomingPayment &&
    currentPlan.upcomingPayment.discountApplied > 0;

  const isActive = currentPlan.status === "active";
  const isTrialing = currentPlan.status === "trialing";
  const isCanceled = currentPlan.status === "canceled";

  const getStatusColor = () => {
    switch (currentPlan.status) {
      case "active":
        return "text-green-600 bg-green-50 border-green-200";
      case "trialing":
        return "text-blue-600 bg-blue-50 border-blue-200";
      case "canceled":
        return "text-red-600 bg-red-50 border-red-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getStatusIcon = () => {
    switch (currentPlan.status) {
      case "active":
        return <CheckCircle2 className="w-4 h-4" />;
      case "trialing":
        return <Clock className="w-4 h-4" />;
      case "canceled":
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    // <Card className="w-full max-w-4xl mx-auto ">
    <Card className="w-full max-w-4xl mx-auto rounded-lg border border-gray-200 overflow-hidden">
      {/* Back to Settings Button */}
      
      <div className="p-4 bg-white">
        <Button
          onClick={onBackToSettings}
          className="flex items-center gap-2"
          id="back-to-settings"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Button>
      </div>

      {/* Header */}
      <CardHeader>
        <CardTitle>Current Plan</CardTitle>
      </CardHeader>

      {/* Plan Details */}
      <div className="bg-white border-b px-6 pb-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">
              {currentPlan.plan?.name || "Plan Name"}
            </h3>
            <p className="text-gray-600 text-sm">
              {currentPlan.plan?.description || "Your subscription details"}
            </p>
          </div>
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${getStatusColor()}`}
          >
            {getStatusIcon()}
            <span className="text-sm font-semibold capitalize">
              {currentPlan.status}
            </span>
          </div>
        </div>
      </div>

      <CardContent className="p-6 space-y-5 bg-gray-50">
        {/* Last Payment Section */}
        {currentPlan.lastPayment && (
          <div className="bg-white rounded-lg p-5 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-green-600" />
                <h3 className="text-base font-semibold text-gray-800">
                  Last Payment
                </h3>
              </div>
              <span
                className={`text-xs px-2 py-1 rounded-full font-medium ${
                  currentPlan.lastPayment.status === "paid"
                    ? "bg-green-100 text-green-700"
                    : "bg-yellow-100 text-yellow-700"
                }`}
              >
                {currentPlan.lastPayment.status.toUpperCase()}
              </span>
            </div>

            <div className="space-y-3">
              <div className="flex items-baseline gap-3">
                <span className="text-2xl font-bold text-gray-900">
                  {formatCurrency(
                    currentPlan.lastPayment.amountPaid,
                    currentPlan.lastPayment.currency
                  )}
                </span>
                {hasDiscount && (
                  <span className="text-base text-gray-400 line-through">
                    {formatCurrency(
                      currentPlan.lastPayment.originalAmount,
                      currentPlan.lastPayment.currency
                    )}
                  </span>
                )}
              </div>

              {hasDiscount && (
                <div className="flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded">
                  <TrendingDown className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">
                    You saved{" "}
                    {formatCurrency(
                      currentPlan.lastPayment.discountApplied,
                      currentPlan.lastPayment.currency
                    )}{" "}
                    with discount
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>
                  Paid on {formatDate(currentPlan.lastPayment.paymentDate)}
                </span>
              </div>

              {currentPlan.plan?.price && (
                <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                  Plan price: {formatCurrency(Number(currentPlan.plan.price))}
                  /month
                </div>
              )}
            </div>
          </div>
        )}

        {/* Discount Expiry Warning */}
        {hasDiscount && !hasUpcomingDiscount && currentPlan.upcomingPayment && (
          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold text-orange-900 mb-1">
                  Discount Expiring Soon
                </h4>
                <p className="text-sm text-orange-800 leading-relaxed">
                  Your limited-time discount expires on{" "}
                  <strong>
                    {formatDate(currentPlan.upcomingPayment.periodEnd)}
                  </strong>
                  . After that, you'll be charged the full price of{" "}
                  <strong>
                    {formatCurrency(
                      currentPlan.upcomingPayment.originalAmount,
                      currentPlan.upcomingPayment.currency
                    )}
                  </strong>{" "}
                  per billing cycle.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Upcoming Payment Section - Conditional rendering based on discount */}
        {currentPlan.upcomingPayment && (
          <>
            {/* Case 1: Discount Applied - Show "Next Renewal (Discounted Price)" */}
            {hasUpcomingDiscount && (
              <div className="bg-white rounded-lg p-5 border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <h3 className="text-base font-semibold text-gray-800">
                    Next Renewal (Discounted Price)
                  </h3>
                </div>

                <div className="space-y-3">
                  <div className="flex items-baseline gap-3">
                    <span className="text-2xl font-bold text-gray-900">
                      {formatCurrency(
                        currentPlan.upcomingPayment.amountDue,
                        currentPlan.upcomingPayment.currency
                      )}
                    </span>
                    <span className="text-base text-gray-400 line-through">
                      {formatCurrency(
                        currentPlan.upcomingPayment.originalAmount,
                        currentPlan.upcomingPayment.currency
                      )}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded">
                    <TrendingDown className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">
                      Discount of{" "}
                      {formatCurrency(
                        currentPlan.upcomingPayment.discountApplied,
                        currentPlan.upcomingPayment.currency
                      )}{" "}
                      applied
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>
                      Due on {formatDate(currentPlan.upcomingPayment.dueDate)}
                    </span>
                  </div>

                  {currentPlan.plan?.price && (
                    <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                      Plan price: {formatCurrency(Number(currentPlan.plan.price))}
                      /month
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Case 1 (continued): Discount Applied - Show "Regular Price" card */}
            {hasUpcomingDiscount && (
              <div className="bg-white rounded-lg p-5 border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                  <h3 className="text-base font-semibold text-gray-800">
                    Regular Price
                  </h3>
                </div>

                <div className="space-y-3">
                  <div className="flex items-baseline gap-3">
                    <span className="text-2xl font-bold text-gray-900">
                      {formatCurrency(
                        currentPlan.upcomingPayment.originalAmount,
                        currentPlan.upcomingPayment.currency
                      )}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 p-2.5 bg-purple-50 border border-purple-200 rounded">
                    <AlertCircle className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-700">
                      Normal plan price without discount
                    </span>
                  </div>

                  <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                    This is the regular price you&apos;ll be charged after the discount period ends
                  </div>
                </div>
              </div>
            )}

            {/* Case 2: No Discount - Show "Next Renewal" */}
            {!hasUpcomingDiscount && (
              <div className="bg-white rounded-lg p-5 border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <h3 className="text-base font-semibold text-gray-800">
                    Next Renewal
                  </h3>
                </div>

                <div className="space-y-3">
                  <div className="flex items-baseline gap-3">
                    <span className="text-2xl font-bold text-gray-900">
                      {formatCurrency(
                        currentPlan.upcomingPayment.amountDue,
                        currentPlan.upcomingPayment.currency
                      )}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>
                      Due on {formatDate(currentPlan.upcomingPayment.dueDate)}
                    </span>
                  </div>

                  {currentPlan.plan?.price && (
                    <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                      Plan price: {formatCurrency(Number(currentPlan.plan.price))}
                      /month
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Fallback for when no payment data exists */}
        {!currentPlan.lastPayment && !currentPlan.upcomingPayment && (
          <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
            <div className="flex flex-col items-center gap-3">
              <div className="p-4 bg-gray-100 rounded-full">
                <CreditCard className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600">No payment information available</p>
              {isTrialing && (
                <p className="text-sm text-blue-600">
                  You're currently on a free trial. Billing will begin on{" "}
                  {formatDate(currentPlan.end)}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Status-specific messages */}
        {isTrialing && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Enjoy your free trial!</strong> Your trial is active from{" "}
              {formatDate(currentPlan.start)} until{" "}
              {formatDate(currentPlan.end)}. Normal pricing will begin after the
              trial ends.
            </p>
          </div>
        )}

        {isCanceled && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">
              <strong>Your plan is cancelled.</strong> You'll have access until{" "}
              {formatDate(currentPlan.end)}. Please{" "}
              <button
                onClick={onNavigateToHelp}
                className="underline font-semibold hover:text-red-900"
              >
                contact us
              </button>{" "}
              to re-subscribe.
            </p>
          </div>
        )}

        {/* Action Button */}
        <div className="flex justify-center pt-2">
          <Button
            onClick={onChangePlan}
            disabled={(!isActive && !isTrialing) || isImpersonated}
            className="w-full md:w-auto"
          >
            {isActive || isTrialing ? "Change Plan" : "Resubscribe"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CurrentPlanCard;
